/**
 * One shared number-formatting layer (design/reshape/RESHAPE.md §4) —
 * currency, percent, signed tone, and counts. Every snapshot value is
 * already a Decimal-as-string or a `number | null` (never fabricated,
 * §A.1) — these functions only ever change how an existing value is
 * *displayed*, never what's fetched or stored: unparsable/`null` input
 * always degrades to the existing "–" convention (`STAT_TOOLTIPS` callers
 * already used a bare `"-"` for this; kept identical here so no panel's
 * honesty behavior changes), never `NaN`, never a guessed `0`.
 *
 * Rule from the dataviz skill (design/reshape/RESHAPE.md §4/§5): color never
 * carries meaning alone — every "up"/"down" tone is paired with an explicit
 * `+`/`−` sign character in the formatted text itself, so a colorblind
 * reader (or a plain-text/CSV export) still gets the direction.
 */

const MISSING = "–"; // en dash, the existing convention for "no value"

export type ValueTone = "up" | "down" | "flat";

export interface ToneText {
  readonly text: string;
  readonly tone: ValueTone;
}

function parseNumeric(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

const THOUSANDS = /\B(?=(\d{3})+(?!\d))/g;

function withThousands(fixed: string): string {
  const negative = fixed.startsWith("-");
  const body = negative ? fixed.slice(1) : fixed;
  const [whole, frac] = body.split(".");
  const groupedWhole = (whole ?? "0").replace(THOUSANDS, ",");
  return `${negative ? "-" : ""}${groupedWhole}${frac !== undefined ? `.${frac}` : ""}`;
}

/** Sign/tone decided from the *rounded* display magnitude, not the raw
 * float — a real value that rounds to zero at the chosen precision (e.g.
 * `-1.67e-5` at 2 decimals) must never render a stray "-0.00%"/"-$0.00": a
 * sign on what reads as zero looks like a data glitch, not "a true zero
 * gets no sign clutter" (this module's own rule, restated correctly here).
 * Returns the already-`toFixed`'d magnitude string alongside the tone/sign
 * so callers never format the value twice. */
function signedParts(
  n: number,
  scale: number,
  decimals: number,
): { readonly magnitude: string; readonly sign: "" | "+" | "-"; readonly tone: ValueTone } {
  const magnitude = Math.abs(n * scale).toFixed(decimals);
  if (Number(magnitude) === 0) return { magnitude, sign: "", tone: "flat" };
  return { magnitude, sign: n > 0 ? "+" : "-", tone: n > 0 ? "up" : "down" };
}

/** `"100484.38"` -> `"$100,484.38"`. Unsigned — for a value whose sign is
 * never meaningful (NAV, cash, counts of dollars rather than a delta). The
 * `-` prefix (on a genuinely negative balance) is decided from the
 * *rounded* magnitude, same rule as the signed formatters below — a value
 * that rounds to zero at `decimals` (e.g. `-0.001` at 2 decimals) must
 * never render a stray "-$0.00". */
export function formatMoney(value: string | number | null | undefined, decimals = 2): string {
  const n = parseNumeric(value);
  if (n === null) return MISSING;
  const magnitude = Math.abs(n).toFixed(decimals);
  const sign = n < 0 && Number(magnitude) !== 0 ? "-" : "";
  return `${sign}$${withThousands(magnitude)}`;
}

/** `"49999.10"` -> `{text: "+$49,999.10", tone: "up"}`; `"-12.50"` ->
 * `{text: "-$12.50", tone: "down"}`. For P&L-flavored money where the sign
 * is the entire point. A value that rounds to zero at `decimals` is
 * `"flat"` with no `+`/`-` clutter. */
export function formatSignedMoney(
  value: string | number | null | undefined,
  decimals = 2,
): ToneText {
  const n = parseNumeric(value);
  if (n === null) return { text: MISSING, tone: "flat" };
  const { magnitude, sign, tone } = signedParts(n, 1, decimals);
  return { text: `${sign}$${withThousands(magnitude)}`, tone };
}

/** A raw fraction (`-0.0010`) as a percent string (`"-0.10%"`). Fractions,
 * not already-scaled percentages — every *bounded* 0..1-style fraction in
 * the snapshot contract (verified against `LifeProjection.*_share`) reads
 * this way. Unsigned/untoned — for a plain proportion with no "good/bad"
 * direction of its own. A value that rounds to zero at `decimals` (e.g.
 * floating-point noise on a share that should be exactly 0) never gets a
 * stray `-` — `toFixed` alone on a small negative float can print "-0.00",
 * which this guards against the same way the signed formatters do. */
export function formatPercent(value: number | null | undefined, decimals = 2): string {
  const n = parseNumeric(value ?? null);
  if (n === null) return MISSING;
  const magnitude = Math.abs(n * 100).toFixed(decimals);
  const sign = n < 0 && Number(magnitude) !== 0 ? "-" : "";
  return `${sign}${magnitude}%`;
}

/** A signed return-like fraction (`0.0123` -> `{text: "+1.23%", tone: "up"}`)
 * — for a delta whose sign is the point (a return, an excess return). Not for
 * drawdowns (see `formatMagnitudePercent` below). */
export function formatSignedPercent(value: number | null | undefined, decimals = 2): ToneText {
  const n = parseNumeric(value ?? null);
  if (n === null) return { text: MISSING, tone: "flat" };
  const { magnitude, sign, tone } = signedParts(n, 100, decimals);
  return { text: `${sign}${magnitude}%`, tone };
}

/** Drawdown-shaped fields: `PerformanceDocument.drawdown` /
 * `FleetOrganismEntry.drawdown` are a **non-negative magnitude** from the
 * running peak (`organism/observatory/performance.py`:
 * `drawdown = (running_max - resources) / running_max`, always >= 0 by
 * construction — 0 at the peak, larger the further below it), never a
 * signed delta. `formatSignedPercent` would invert the meaning here: a real
 * 8% drawdown has `n = 0.08 > 0`, which that function tones "up"/green,
 * reading as a *gain* on the one field whose job is to flag risk. This
 * shows the unsigned magnitude, toned "down" (the existing `--obs-bad` red)
 * whenever it's meaningfully above zero, "flat" only exactly at the peak. */
export function formatMagnitudePercent(value: number | null | undefined, decimals = 2): ToneText {
  const n = parseNumeric(value ?? null);
  if (n === null) return { text: MISSING, tone: "flat" };
  const magnitude = Math.abs(n * 100).toFixed(decimals);
  return { text: `${magnitude}%`, tone: Number(magnitude) === 0 ? "flat" : "down" };
}

/** Signed *plain number* (no currency/percent scaling) — for a dimensionless
 * signed quantity like reward/log-growth
 * (`organism/observatory/publish.py`: `reward_log_growth_cum += ln(w_t/w_{t-1})`,
 * an unbounded cumulative sum, never a 0..1 fraction). `formatSignedPercent`
 * would misrepresent this by multiplying an already-cumulative log-growth
 * value by 100 as though it were a bounded fraction; this instead keeps the
 * value's own scale and just adds the sign/tone/tabular formatting. */
export function formatSignedNumber(value: number | null | undefined, decimals = 6): ToneText {
  const n = parseNumeric(value ?? null);
  if (n === null) return { text: MISSING, tone: "flat" };
  const { magnitude, sign, tone } = signedParts(n, 1, decimals);
  return { text: `${sign}${magnitude}`, tone };
}

/** Thousands-separated integer count (positions, orders) — these are
 * genuine whole-number counts from the snapshot, so truncating is exact,
 * not lossy. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return MISSING;
  return withThousands(Math.trunc(value).toFixed(0));
}

/** Thousands-separated *decimal* number, `decimals` places preserved (never
 * truncated) — for a field that looks like a count but is actually a real
 * float, where `formatCount`'s `Math.trunc` would silently destroy a
 * meaningful distinction. `LifeProjection.median_cycles`/`p10_cycles`/
 * `p90_cycles` are exactly this: `organism/observatory/life.py` computes
 * them as `float(np.median(...))`, so e.g. `0.9` (under a decision cycle
 * left) truncating to `formatCount`'s `"0"` would be indistinguishable from
 * a real `0` (no cycles left) on the one panel whose job is projecting how
 * much time is left. */
export function formatDecimal(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return MISSING;
  return withThousands(value.toFixed(decimals));
}

/** Decimal places a unit PRICE needs to stay meaningful (OQA, 2026-10-02): cents
 * are fine for a $329 share, but the crypto price grid is 1e-9 — ADA at $0.2503
 * rendered "0.25 / 0.25 · spread 0.00" and a DOGE fill at $0.145069 "$0.15". */
export function priceDecimals(magnitude: number): number {
  if (magnitude >= 100) return 2;
  if (magnitude >= 1) return 4;
  if (magnitude >= 0.01) return 6;
  return 9;
}

/** Trims trailing zeros past `minDecimals` ("0.250300" -> "0.2503", "84.50" stays). */
function trimFraction(fixed: string, minDecimals: number): string {
  const dot = fixed.indexOf(".");
  if (dot < 0) return fixed;
  let end = fixed.length;
  while (end > dot + 1 + minDecimals && fixed[end - 1] === "0") end -= 1;
  if (end === dot + 1) end = dot;
  return fixed.slice(0, end);
}

/** A unit price at the precision its magnitude needs (2 decimals from $100, 4 from
 * $1, 6 below, 9 below a cent), trailing zeros trimmed to at least 2 places,
 * thousands-separated; `{ currency: true }` prefixes `$`. A string input is
 * parsed once, never re-rounded to cents first. */
export function formatPrice(
  value: string | number | null | undefined,
  options: { readonly currency?: boolean } = {},
): string {
  const n = parseNumeric(value);
  if (n === null) return MISSING;
  const magnitude = Math.abs(n);
  const body = trimFraction(magnitude.toFixed(priceDecimals(magnitude)), 2);
  const sign = n < 0 && Number(body) !== 0 ? "-" : "";
  return `${sign}${options.currency ? "$" : ""}${withThousands(body)}`;
}

/** A position/fill QUANTITY as recorded: whole shares stay whole ("21", not
 * "21.0000"), fractional crypto keeps its digits up to the 1e-9 grid
 * ("0.000123456"), trailing zeros trimmed. A Decimal string is used verbatim
 * (no float round-trip) when it is already a plain decimal. */
export function formatQuantity(value: string | number | null | undefined): string {
  let body: string;
  if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim())) {
    body = trimFraction(value.trim(), 0);
  } else {
    const n = parseNumeric(value);
    if (n === null) return MISSING;
    body = trimFraction(n.toFixed(9), 0);
  }
  if (Number(body) === 0) body = body.replace(/^-/, "");
  return withThousands(body);
}

/** A value's percentage of `cap`, clamped to `[0, 100]` — the one shared
 * "how full is this capped bar" computation (code review finding: the fleet
 * screen's drawdown bar and the sizing panel's weight/gross bars each had
 * their own inline copy of this same clamp-and-scale math, which could
 * silently drift apart). Returns `0` (an empty bar, never a fabricated
 * partial fill) whenever `value` is missing/non-finite or `cap` is
 * non-positive. */
export function cappedPercent(value: number | null | undefined, cap: number): number {
  if (value === null || value === undefined || !Number.isFinite(value) || cap <= 0) return 0;
  return Math.max(0, Math.min(100, (value / cap) * 100));
}

/** CSS class for a `ToneText.tone` — the existing closed palette
 * (`--obs-good`/`--obs-bad`), never a new color. */
export function toneClass(tone: ValueTone): string {
  if (tone === "up") return "obs-value-up";
  if (tone === "down") return "obs-value-down";
  return "obs-value-flat";
}
